import React, { useState, useEffect, useRef } from 'react';
import { Building2, Plus, Trash2, ChevronDown, ChevronUp, Layers, DollarSign, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { buildingMaterialsDatabase } from '../data/buildingMaterials';

const BuildingCalculator = ({ buildings, setBuildings }) => {
  const [showForm, setShowForm] = useState(false);
  const [costPerTon, setCostPerTon] = useState('');
  const [activeFloorTab, setActiveFloorTab] = useState('1');
  const prevNumStoriesRef = useRef(null);
  
  // Initialize floor data for individual floor configuration
  const initializeFloors = (numStories) => {
    const floors = {};
    for (let i = 1; i <= numStories; i++) {
      floors[i] = {
        floorArea: '',
        floorMaterial: null,
        ceilingArea: '',
        ceilingMaterial: null,
        ceilingStructure: null,
        intWallLength: '',
        intWallHeight: '',
        intStructure: null,
        intInsulation: null,
        intFinishSide1: null,
        intFinishSide2: null,
      };
    }
    return floors;
  };

  const [currentBuilding, setCurrentBuilding] = useState({
    name: '',
    numStories: '1',
    // External Walls (whole building)
    extWallLength: '',
    extWallHeight: '',
    extFinish: null,
    extStructure: null,
    extInsulation: null,
    extInteriorFinish: null,
    // Per-floor data
    floors: initializeFloors(1),
    // Roof (top floor only)
    roofArea: '',
    roofCovering: null,
    roofStructure: null,
    // Option to use same floor config for all floors
    useUniformFloors: true
  });

  // Update floors when number of stories changes
  useEffect(() => {
    const numStories = parseInt(currentBuilding.numStories) || 1;
    
    // Skip if numStories hasn't changed
    if (prevNumStoriesRef.current === numStories) return;
    prevNumStoriesRef.current = numStories;
    
    const currentFloors = currentBuilding.floors;
    const newFloors = {};
    
    for (let i = 1; i <= numStories; i++) {
      if (currentFloors[i]) {
        newFloors[i] = currentFloors[i];
      } else {
        // Copy from floor 1 if uniform, otherwise initialize empty
        if (currentBuilding.useUniformFloors && currentFloors[1]) {
          newFloors[i] = { ...currentFloors[1] };
        } else {
          newFloors[i] = {
            floorArea: '',
            floorMaterial: null,
            ceilingArea: '',
            ceilingMaterial: null,
            ceilingStructure: null,
            intWallLength: '',
            intWallHeight: '',
            intStructure: null,
            intInsulation: null,
            intFinishSide1: null,
            intFinishSide2: null,
          };
        }
      }
    }
    
    setCurrentBuilding(prev => ({ ...prev, floors: newFloors }));
    
    // Reset active floor tab if it exceeds new story count
    if (parseInt(activeFloorTab) > numStories) {
      setActiveFloorTab('1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBuilding.numStories]);

  const handleInputChange = (field, value) => {
    setCurrentBuilding(prev => ({ ...prev, [field]: value }));
  };

  const handleFloorInputChange = (floorNum, field, value) => {
    setCurrentBuilding(prev => {
      const updatedFloors = { ...prev.floors };
      updatedFloors[floorNum] = { ...updatedFloors[floorNum], [field]: value };
      
      // If uniform floors is on and this is floor 1, copy to all floors
      if (prev.useUniformFloors && floorNum === 1) {
        const numStories = parseInt(prev.numStories) || 1;
        for (let i = 2; i <= numStories; i++) {
          updatedFloors[i] = { ...updatedFloors[floorNum] };
        }
      }
      
      return { ...prev, floors: updatedFloors };
    });
  };

  const handleMaterialSelect = (field, materialName, category) => {
    const material = buildingMaterialsDatabase[category].find(m => m.name === materialName);
    setCurrentBuilding(prev => ({ ...prev, [field]: material }));
  };

  const handleFloorMaterialSelect = (floorNum, field, materialName, category) => {
    const material = buildingMaterialsDatabase[category].find(m => m.name === materialName);
    setCurrentBuilding(prev => {
      const updatedFloors = { ...prev.floors };
      updatedFloors[floorNum] = { ...updatedFloors[floorNum], [field]: material };
      
      // If uniform floors is on and this is floor 1, copy to all floors
      if (prev.useUniformFloors && floorNum === 1) {
        const numStories = parseInt(prev.numStories) || 1;
        for (let i = 2; i <= numStories; i++) {
          updatedFloors[i] = { ...updatedFloors[i], [field]: material };
        }
      }
      
      return { ...prev, floors: updatedFloors };
    });
  };

  const toggleUniformFloors = () => {
    setCurrentBuilding(prev => {
      const newValue = !prev.useUniformFloors;
      
      // If turning on uniform, copy floor 1 to all floors
      if (newValue && prev.floors[1]) {
        const numStories = parseInt(prev.numStories) || 1;
        const updatedFloors = {};
        for (let i = 1; i <= numStories; i++) {
          updatedFloors[i] = { ...prev.floors[1] };
        }
        return { ...prev, useUniformFloors: newValue, floors: updatedFloors };
      }
      
      return { ...prev, useUniformFloors: newValue };
    });
  };

  const calculateBuildingWeight = (building) => {
    const materialTotals = {};
    const numStories = parseInt(building.numStories) || 1;
    const floorBreakdown = {}; // Track by floor

    const addToMaterialTotal = (materialName, squareFeet, weightPerSqFt, floorNum = null) => {
      if (!materialTotals[materialName]) {
        materialTotals[materialName] = {
          name: materialName,
          squareFeet: 0,
          weightPerSqFt: weightPerSqFt,
          totalPounds: 0,
          totalTons: 0
        };
      }
      materialTotals[materialName].squareFeet += squareFeet;
      materialTotals[materialName].totalPounds += squareFeet * weightPerSqFt;
      materialTotals[materialName].totalTons = materialTotals[materialName].totalPounds / 2000;

      // Track per floor
      if (floorNum) {
        if (!floorBreakdown[floorNum]) {
          floorBreakdown[floorNum] = { totalPounds: 0, totalTons: 0, materials: [] };
        }
        const pounds = squareFeet * weightPerSqFt;
        floorBreakdown[floorNum].totalPounds += pounds;
        floorBreakdown[floorNum].totalTons = floorBreakdown[floorNum].totalPounds / 2000;
        floorBreakdown[floorNum].materials.push({
          name: materialName,
          squareFeet,
          weightPerSqFt,
          pounds,
          tons: pounds / 2000
        });
      }
    };

    // External Walls (full building)
    if (building.extWallLength && building.extWallHeight) {
      const extSqFtPerStory = parseFloat(building.extWallLength) * parseFloat(building.extWallHeight);
      
      for (let floor = 1; floor <= numStories; floor++) {
        if (building.extFinish && building.extFinish.weightPerSqFt > 0) {
          addToMaterialTotal(`Ext Wall - ${building.extFinish.name}`, extSqFtPerStory, building.extFinish.weightPerSqFt, floor);
        }
        if (building.extStructure && building.extStructure.weightPerSqFt > 0) {
          addToMaterialTotal(`Ext Wall - ${building.extStructure.name}`, extSqFtPerStory, building.extStructure.weightPerSqFt, floor);
        }
        if (building.extInsulation && building.extInsulation.weightPerSqFt > 0) {
          addToMaterialTotal(`Ext Wall - ${building.extInsulation.name}`, extSqFtPerStory, building.extInsulation.weightPerSqFt, floor);
        }
        if (building.extInteriorFinish && building.extInteriorFinish.weightPerSqFt > 0) {
          addToMaterialTotal(`Ext Wall - ${building.extInteriorFinish.name}`, extSqFtPerStory, building.extInteriorFinish.weightPerSqFt, floor);
        }
      }
    }

    // Per-floor components
    for (let floor = 1; floor <= numStories; floor++) {
      const floorData = building.floors[floor];
      if (!floorData) continue;

      // Interior Walls
      if (floorData.intWallLength && floorData.intWallHeight) {
        const intSqFt = parseFloat(floorData.intWallLength) * parseFloat(floorData.intWallHeight);
        
        if (floorData.intStructure && floorData.intStructure.weightPerSqFt > 0) {
          addToMaterialTotal(`Int Wall - ${floorData.intStructure.name}`, intSqFt, floorData.intStructure.weightPerSqFt, floor);
        }
        if (floorData.intInsulation && floorData.intInsulation.weightPerSqFt > 0) {
          addToMaterialTotal(`Int Wall - ${floorData.intInsulation.name}`, intSqFt, floorData.intInsulation.weightPerSqFt, floor);
        }
        if (floorData.intFinishSide1 && floorData.intFinishSide1.weightPerSqFt > 0) {
          addToMaterialTotal(`Int Wall - ${floorData.intFinishSide1.name} (S1)`, intSqFt, floorData.intFinishSide1.weightPerSqFt, floor);
        }
        if (floorData.intFinishSide2 && floorData.intFinishSide2.weightPerSqFt > 0) {
          addToMaterialTotal(`Int Wall - ${floorData.intFinishSide2.name} (S2)`, intSqFt, floorData.intFinishSide2.weightPerSqFt, floor);
        }
      }

      // Floor
      if (floorData.floorArea && floorData.floorMaterial && floorData.floorMaterial.weightPerSqFt > 0) {
        const floorSqFt = parseFloat(floorData.floorArea);
        addToMaterialTotal(`Floor ${floor} - ${floorData.floorMaterial.name}`, floorSqFt, floorData.floorMaterial.weightPerSqFt, floor);
      }

      // Ceiling
      if (floorData.ceilingArea) {
        const ceilingSqFt = parseFloat(floorData.ceilingArea);
        
        if (floorData.ceilingMaterial && floorData.ceilingMaterial.weightPerSqFt > 0) {
          addToMaterialTotal(`Ceiling ${floor} - ${floorData.ceilingMaterial.name}`, ceilingSqFt, floorData.ceilingMaterial.weightPerSqFt, floor);
        }
        if (floorData.ceilingStructure && floorData.ceilingStructure.weightPerSqFt > 0) {
          addToMaterialTotal(`Ceiling ${floor} - ${floorData.ceilingStructure.name}`, ceilingSqFt, floorData.ceilingStructure.weightPerSqFt, floor);
        }
      }
    }

    // Roof (only once)
    if (building.roofArea) {
      const roofSqFt = parseFloat(building.roofArea);
      
      if (building.roofCovering && building.roofCovering.weightPerSqFt > 0) {
        addToMaterialTotal(`Roof - ${building.roofCovering.name}`, roofSqFt, building.roofCovering.weightPerSqFt, numStories);
      }
      if (building.roofStructure && building.roofStructure.weightPerSqFt > 0) {
        addToMaterialTotal(`Roof - ${building.roofStructure.name}`, roofSqFt, building.roofStructure.weightPerSqFt, numStories);
      }
    }

    const components = Object.values(materialTotals);
    const totalPounds = components.reduce((sum, comp) => sum + comp.totalPounds, 0);
    const totalTons = totalPounds / 2000;

    return { components, totalTons, totalPounds, floorBreakdown };
  };

  const handleAddBuilding = () => {
    if (!currentBuilding.name.trim()) {
      alert('Please enter a building name');
      return;
    }

    const calculation = calculateBuildingWeight(currentBuilding);
    
    if (calculation.totalTons === 0) {
      alert('Please add at least one building component with measurements and materials');
      return;
    }

    const cost = parseFloat(costPerTon) || 0;
    const totalCost = calculation.totalTons * cost;

    const newBuilding = {
      id: Date.now(),
      ...currentBuilding,
      components: calculation.components,
      floorBreakdown: calculation.floorBreakdown,
      totalTons: calculation.totalTons,
      totalPounds: calculation.totalPounds,
      costPerTon: cost,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setBuildings([...buildings, newBuilding]);
    
    // Reset form
    setCurrentBuilding({
      name: '',
      numStories: '1',
      extWallLength: '', extWallHeight: '',
      extFinish: null, extStructure: null, extInsulation: null, extInteriorFinish: null,
      floors: initializeFloors(1),
      roofArea: '', roofCovering: null, roofStructure: null,
      useUniformFloors: true
    });
    setCostPerTon('');
    setShowForm(false);
    setActiveFloorTab('1');
  };

  const handleDeleteBuilding = (id) => {
    setBuildings(buildings.filter(b => b.id !== id));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num);
  };

  const getTotals = () => {
    const totalTons = buildings.reduce((sum, b) => sum + (b.totalTons || 0), 0);
    const totalPounds = totalTons * 2000;
    const buildingCount = buildings.length;
    const totalCost = buildings.reduce((sum, b) => sum + (b.totalCost || 0), 0);
    
    return { buildingCount, totalTons, totalPounds, totalCost };
  };

  const totals = getTotals();
  const numStories = parseInt(currentBuilding.numStories) || 1;

  // Render floor configuration section
  const renderFloorConfig = (floorNum) => {
    const floorData = currentBuilding.floors[floorNum] || {};
    
    return (
      <div className="space-y-4">
        {/* Floor Section */}
        <div className="space-y-3 p-3 border rounded bg-background">
          <h5 className="font-medium text-sm flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Floor {floorNum} - Flooring
          </h5>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Floor Area (sq ft)</Label>
              <Input
                type="number"
                placeholder="0"
                value={floorData.floorArea || ''}
                onChange={(e) => handleFloorInputChange(floorNum, 'floorArea', e.target.value)}
              />
            </div>
            <div>
              <Label>Floor Material</Label>
              <Select 
                value={floorData.floorMaterial?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'floorMaterial', val, 'floor')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.floor.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Ceiling Section */}
        <div className="space-y-3 p-3 border rounded bg-background">
          <h5 className="font-medium text-sm">Floor {floorNum} - Ceiling</h5>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Ceiling Area (sq ft)</Label>
              <Input
                type="number"
                placeholder="0"
                value={floorData.ceilingArea || ''}
                onChange={(e) => handleFloorInputChange(floorNum, 'ceilingArea', e.target.value)}
              />
            </div>
            <div>
              <Label>Material</Label>
              <Select
                value={floorData.ceilingMaterial?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'ceilingMaterial', val, 'ceiling')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.ceiling.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Structure</Label>
              <Select
                value={floorData.ceilingStructure?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'ceilingStructure', val, 'ceilingStructure')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.ceilingStructure.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Interior Walls Section */}
        <div className="space-y-3 p-3 border rounded bg-background">
          <h5 className="font-medium text-sm">Floor {floorNum} - Interior Walls</h5>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Total Wall Length (ft)</Label>
              <Input
                type="number"
                placeholder="0"
                value={floorData.intWallLength || ''}
                onChange={(e) => handleFloorInputChange(floorNum, 'intWallLength', e.target.value)}
              />
            </div>
            <div>
              <Label>Wall Height (ft)</Label>
              <Input
                type="number"
                placeholder="0"
                value={floorData.intWallHeight || ''}
                onChange={(e) => handleFloorInputChange(floorNum, 'intWallHeight', e.target.value)}
              />
            </div>
          </div>
          {floorData.intWallLength && floorData.intWallHeight && (
            <p className="text-xs text-muted-foreground">
              = {formatNumber(parseFloat(floorData.intWallLength) * parseFloat(floorData.intWallHeight))} sq ft
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Wall Structure</Label>
              <Select
                value={floorData.intStructure?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'intStructure', val, 'interiorStructure')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.interiorStructure.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Insulation</Label>
              <Select
                value={floorData.intInsulation?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'intInsulation', val, 'insulation')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.insulation.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finish (Side 1)</Label>
              <Select
                value={floorData.intFinishSide1?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'intFinishSide1', val, 'interiorFinish')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.interiorFinish.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finish (Side 2)</Label>
              <Select
                value={floorData.intFinishSide2?.name || ''}
                onValueChange={(val) => handleFloorMaterialSelect(floorNum, 'intFinishSide2', val, 'interiorFinish')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {buildingMaterialsDatabase.interiorFinish.map((mat) => (
                    <SelectItem key={mat.name} value={mat.name}>
                      {mat.name} ({mat.weightPerSqFt} lb/ft²)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" />
          Custom Building Measurements
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-sm">Configure multi-story buildings with detailed material specifications. Set exterior walls, floors, ceilings, roofing, and interior partitions with specific materials and dimensions.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <p className="text-sm text-muted-foreground">Buildings</p>
            <p className="text-2xl font-bold">{totals.buildingCount}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Weight</p>
            <p className="text-2xl font-bold">{formatNumber(totals.totalTons)} tons</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Pounds</p>
            <p className="text-2xl font-bold">{formatNumber(totals.totalPounds)} lbs</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Cost</p>
            <p className="text-2xl font-bold text-green-600">${formatNumber(totals.totalCost)}</p>
          </div>
        </div>

        {/* Add Building Button */}
        <Button
          onClick={() => setShowForm(!showForm)}
          className="w-full"
          variant={showForm ? "outline" : "default"}
        >
          {showForm ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Hide Form
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Add Building
            </>
          )}
        </Button>

        {/* Add Building Form */}
        {showForm && (
          <div className="space-y-6 p-4 border rounded-lg bg-card">
            {/* Building Name, Stories, and Cost */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="buildingName">Building Name *</Label>
                <Input
                  id="buildingName"
                  placeholder="e.g., Main Building"
                  value={currentBuilding.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="numStories">Number of Stories *</Label>
                <Input
                  id="numStories"
                  type="number"
                  min="1"
                  max="20"
                  placeholder="1"
                  value={currentBuilding.numStories}
                  onChange={(e) => handleInputChange('numStories', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="costPerTon">Cost per Ton</Label>
                <Input
                  id="costPerTon"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="$0.00"
                  value={costPerTon}
                  onChange={(e) => setCostPerTon(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant={currentBuilding.useUniformFloors ? "default" : "outline"}
                  size="sm"
                  className="w-full"
                  onClick={toggleUniformFloors}
                >
                  {currentBuilding.useUniformFloors ? '✓ Same All Floors' : 'Different Per Floor'}
                </Button>
              </div>
            </div>

            {/* External Walls */}
            <div className="space-y-3 p-3 border rounded bg-muted/50">
              <h4 className="font-semibold">External Walls (Entire Building)</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Perimeter Length (ft)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={currentBuilding.extWallLength}
                    onChange={(e) => handleInputChange('extWallLength', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Height per Story (ft)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={currentBuilding.extWallHeight}
                    onChange={(e) => handleInputChange('extWallHeight', e.target.value)}
                  />
                </div>
              </div>

              {currentBuilding.extWallLength && currentBuilding.extWallHeight && (
                <p className="text-sm text-muted-foreground">
                  = {formatNumber(parseFloat(currentBuilding.extWallLength) * parseFloat(currentBuilding.extWallHeight) * numStories)} sq ft total (all {numStories} stories)
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Exterior Finish</Label>
                  <Select 
                    onValueChange={(val) => handleMaterialSelect('extFinish', val, 'exteriorFinish')}
                    value={currentBuilding.extFinish?.name}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select finish" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingMaterialsDatabase.exteriorFinish.map((mat) => (
                        <SelectItem key={mat.name} value={mat.name}>
                          {mat.name} ({mat.weightPerSqFt} lb/ft²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Wall Structure</Label>
                  <Select onValueChange={(val) => handleMaterialSelect('extStructure', val, 'exteriorStructure')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select structure" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingMaterialsDatabase.exteriorStructure.map((mat) => (
                        <SelectItem key={mat.name} value={mat.name}>
                          {mat.name} ({mat.weightPerSqFt} lb/ft²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Insulation</Label>
                  <Select onValueChange={(val) => handleMaterialSelect('extInsulation', val, 'insulation')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select insulation" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingMaterialsDatabase.insulation.map((mat) => (
                        <SelectItem key={mat.name} value={mat.name}>
                          {mat.name} ({mat.weightPerSqFt} lb/ft²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Interior Finish</Label>
                  <Select onValueChange={(val) => handleMaterialSelect('extInteriorFinish', val, 'interiorFinish')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select finish" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingMaterialsDatabase.interiorFinish.map((mat) => (
                        <SelectItem key={mat.name} value={mat.name}>
                          {mat.name} ({mat.weightPerSqFt} lb/ft²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Per-Floor Configuration */}
            <div className="space-y-3 p-3 border rounded bg-muted/50">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Floor-by-Floor Configuration
                </h4>
                {!currentBuilding.useUniformFloors && numStories > 1 && (
                  <Badge variant="outline">Configuring each floor separately</Badge>
                )}
              </div>

              {currentBuilding.useUniformFloors ? (
                // Uniform floors - show single config that applies to all
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground mb-3">
                    Configure once - applies to all {numStories} floor(s). 
                    <button className="text-primary hover:underline ml-1" onClick={toggleUniformFloors}>
                      Click to configure each floor separately
                    </button>
                  </p>
                  {renderFloorConfig(1)}
                </div>
              ) : (
                // Individual floor tabs
                <Tabs value={activeFloorTab} onValueChange={setActiveFloorTab}>
                  <TabsList className="flex flex-wrap h-auto gap-1">
                    {Array.from({ length: numStories }, (_, i) => i + 1).map(floor => (
                      <TabsTrigger key={floor} value={String(floor)} className="text-xs px-3 py-1">
                        Floor {floor}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  {Array.from({ length: numStories }, (_, i) => i + 1).map(floor => (
                    <TabsContent key={floor} value={String(floor)} className="mt-3">
                      {renderFloorConfig(floor)}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </div>

            {/* Roof (top floor only) */}
            <div className="space-y-3 p-3 border rounded bg-muted/50">
              <h4 className="font-semibold">Roof (Top Floor Only)</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Area (sq ft)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={currentBuilding.roofArea}
                    onChange={(e) => handleInputChange('roofArea', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Covering</Label>
                  <Select onValueChange={(val) => handleMaterialSelect('roofCovering', val, 'roofCovering')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingMaterialsDatabase.roofCovering.map((mat) => (
                        <SelectItem key={mat.name} value={mat.name}>
                          {mat.name} ({mat.weightPerSqFt} lb/ft²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Structure</Label>
                  <Select onValueChange={(val) => handleMaterialSelect('roofStructure', val, 'roofStructure')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingMaterialsDatabase.roofStructure.map((mat) => (
                        <SelectItem key={mat.name} value={mat.name}>
                          {mat.name} ({mat.weightPerSqFt} lb/ft²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Button onClick={handleAddBuilding} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Building
            </Button>
          </div>
        )}

        {/* Buildings List */}
        {buildings.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold">Saved Buildings</h4>
            {buildings.map((building) => (
              <Card key={building.id} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h5 className="font-semibold">{building.name}</h5>
                    <p className="text-sm text-muted-foreground">
                      {building.numStories} {parseInt(building.numStories) === 1 ? 'story' : 'stories'} • {formatNumber(building.totalTons)} tons ({formatNumber(building.totalPounds)} lbs)
                      {building.totalCost > 0 && (
                        <span className="text-green-600 ml-2">• ${formatNumber(building.totalCost)}</span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteBuilding(building.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
                
                {/* Floor Breakdown */}
                {building.floorBreakdown && Object.keys(building.floorBreakdown).length > 0 && (
                  <div className="mb-3">
                    <p className="font-semibold text-xs text-muted-foreground mb-2">Per-Floor Breakdown:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(building.floorBreakdown).map(([floor, data]) => (
                        <div key={floor} className="bg-muted/50 p-2 rounded text-center">
                          <p className="text-xs text-muted-foreground">Floor {floor}</p>
                          <p className="font-semibold text-accent">{formatNumber(data.totalTons)} tons</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-1 text-sm max-h-48 overflow-y-auto">
                  <p className="font-semibold text-xs text-muted-foreground mb-2">Material Breakdown:</p>
                  {building.components.map((comp, idx) => (
                    <div key={idx} className="flex justify-between items-center py-1 border-b border-border/50">
                      <div className="flex-1">
                        <span className="text-foreground">{comp.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          ({comp.squareFeet.toFixed(0)} ft² × {comp.weightPerSqFt} lb/ft²)
                        </span>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-accent">{formatNumber(comp.totalTons)} tons</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {buildings.length === 0 && !showForm && (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No buildings added yet</p>
            <p className="text-sm">Click &quot;Add Building&quot; to get started</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuildingCalculator;
